import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";

const Dropdown = ({
    trigger,
    items = [],
    isOpen: controlledIsOpen,
    onToggle,
    className = "",
    triggerClassName = "",
    position = "bottom-left",
    width = "w-48",
    onItemClick = () => {},
    closeOnItemClick = true,
    disabled = false,
    centerDropdown = true,
}) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
    const setIsOpen = controlledIsOpen !== undefined ? onToggle : setInternalIsOpen;

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [isOpen, setIsOpen]);

    // Close on escape
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === "Escape" && isOpen) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            return () => document.removeEventListener("keydown", handleEscape);
        }
    }, [isOpen, setIsOpen]);

    const getPositionClasses = () => {
        switch (position) {
            case "right":
                return "right-full top-0";
            case "bottom-right":
                return "right-0 top-full";
            case "top-left":
                return "left-0 bottom-full";
            case "top-right":
                return "right-0 bottom-full";
            case "bottom-left":
            default:
                return "left-0 top-full";
        }
    };

    const renderItem = (item, index) => {
        // Separator
        if (item.type === "separator" || item.separator) {
            return (
                <div key={item.key || `separator-${index}`} className="my-1">
                    <div className="h-px bg-base-300 mx-3"></div>
                </div>
            );
        }

        // Custom content
        if (item.custom) {
            return (
                <div key={item.key || `custom-${index}`} className="px-1">
                    {typeof item.custom === "function" ? item.custom(item, index) : item.custom}
                </div>
            );
        }

        // Regular items
        const handleClick = (e) => {
            if (item.disabled) {
                e.preventDefault();
                return;
            }

            if (item.onClick) {
                item.onClick(e, item);
            }

            onItemClick(item, e);

            if (closeOnItemClick) {
                setIsOpen(false);
            }
        };

        const itemClasses = `
            flex items-center justify-between w-full px-3 py-2
            text-sm text-base-content cursor-pointer
            rounded-md transition-colors duration-150 ease-in-out
            ${
                item.disabled
                    ? "opacity-50 cursor-not-allowed"
                    : item.active
                      ? "bg-primary/15 text-primary font-medium"
                      : "hover:bg-primary/8"
            }
        `
            .replace(/\s+/g, " ")
            .trim();

        const content = (
            <>
                <span>{item.label}</span>
                {item.active && <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>}
            </>
        );

        return (
            <div key={item.key || `item-${index}`} className="px-1">
                {item.to ? (
                    <Link to={item.to} className={itemClasses} onClick={handleClick}>
                        {content}
                    </Link>
                ) : (
                    <button
                        className={itemClasses}
                        onClick={handleClick}
                        disabled={item.disabled}
                        type="button"
                    >
                        {content}
                    </button>
                )}
            </div>
        );
    };

    const handleTriggerClick = (e) => {
        if (disabled) return;
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    const dropdownContent = (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <div
                className={triggerClassName}
                onClick={handleTriggerClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleTriggerClick(e);
                    }
                }}
            >
                {trigger}
            </div>

            <div
                className={`
                    absolute z-39 ${width} ${getPositionClasses()}
                    bg-base-300 border border-primary/10 rounded-lg shadow-lg shadow-primary/5
                    py-2 transition-all duration-300 ease-in-out
                    ${
                        isOpen
                            ? "opacity-100 scale-100 translate-y-0"
                            : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                    }
                `}
            >
                <div
                    className={`
                        transition-all duration-300 ease-in-out
                        ${isOpen ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0"}
                    `}
                >
                    {items.length === 0 ? (
                        <div className="px-4 py-4 text-center">
                            <div className="text-base-content/60 text-sm">No items available</div>
                        </div>
                    ) : (
                        <div className="space-y-0.5">
                            {items
                                .sort((a, b) => {
                                    // Sort by position if both items have position property
                                    if (a.position !== undefined && b.position !== undefined) {
                                        return a.position - b.position;
                                    }
                                    // Keep original order if positions are not defined
                                    return 0;
                                })
                                .map((item, index) => renderItem(item, index))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    // Conditionally wrap with centering container
    return centerDropdown ? (
        <div className="flex justify-center">{dropdownContent}</div>
    ) : (
        dropdownContent
    );
};

export default Dropdown;
